// Inline Soulstack Verify widget for forms (sign in, sign up, reset, contact).
// Use `useVerifyGate(siteKey)` for the state and render `<VerifyWidget gate>`
// where the check should appear. The widget solves itself when it can (the
// invisible proof-of-work tier) and shows a challenge when it cannot, so the
// form only ever needs `gate.token`.
//
// Tokens are single use. Call `gate.renew()` after EVERY attempt, successful or
// not, to spend the old token and start a fresh challenge; disable submit while
// `gate.token` is empty. For the modal overlay used by publish and wiki, see
// services/captcha.ts.
import { useCallback, useEffect, useRef, useState } from 'react';

import { createVerifyElement, loadVerifyWidget } from '../services/captcha';

/** How long the widget may go without a token before the form says so. */
const STALL_MS = 30_000;

export interface VerifyGate {
  /** One-use token, '' until solved and again once spent, expired or renewed. */
  token: string;
  /** The widget script could not be loaded at all (offline, blocked). */
  unavailable: boolean;
  /** No token yet after STALL_MS: Verify is slow or unreachable. */
  stalled: boolean;
  /** Spend the current token and start a fresh challenge. */
  renew: () => void;
  /** Ref callback for the element the widget mounts into. The widget exists
   * only while that element does, so rendering it conditionally works. */
  hostRef: (node: HTMLDivElement | null) => void;
}

export function useVerifyGate(siteKey: string): VerifyGate {
  const [host, setHost] = useState<HTMLDivElement | null>(null);
  const [token, setToken] = useState('');
  const [unavailable, setUnavailable] = useState(false);
  const [stalled, setStalled] = useState(false);
  // Bumped by renew() so the stall timer restarts with the new challenge.
  const [round, setRound] = useState(0);
  const widgetRef = useRef<ReturnType<typeof createVerifyElement> | null>(null);

  useEffect(() => {
    if (!host) return;
    let cancelled = false;
    // The custom element extends HTMLElement, so it is only ever loaded here,
    // client side, never at module scope.
    loadVerifyWidget()
      .then(() => {
        if (cancelled) return;
        const widget = createVerifyElement(siteKey);
        widget.addEventListener('sstk-verified', (event) => {
          setToken((event as CustomEvent<{ token: string }>).detail.token);
        });
        // The widget resets itself to idle when its token expires; the form
        // must stop offering the dead token.
        widget.addEventListener('sstk-expired', () => setToken(''));
        host.append(widget);
        widgetRef.current = widget;
      })
      .catch(() => {
        if (!cancelled) setUnavailable(true);
      });
    return () => {
      cancelled = true;
      widgetRef.current?.remove();
      widgetRef.current = null;
      setToken('');
      setUnavailable(false);
    };
  }, [host, siteKey]);

  useEffect(() => {
    if (!host || token) return;
    const timer = setTimeout(() => setStalled(true), STALL_MS);
    return () => {
      clearTimeout(timer);
      setStalled(false);
    };
  }, [host, token, round]);

  const renew = useCallback(() => {
    setToken('');
    setRound((r) => r + 1);
    widgetRef.current?.refresh();
  }, []);

  return { token, unavailable, stalled, renew, hostRef: setHost };
}

export function VerifyWidget({ gate }: { gate: VerifyGate }) {
  return (
    <div>
      <div ref={gate.hostRef} />
      {(gate.unavailable || gate.stalled) && (
        <p role="alert" style={{ color: 'var(--ss-danger)', fontSize: 13, lineHeight: 1.5, marginTop: 8 }}>
          {gate.unavailable
            ? 'The verification check could not load. Check your connection and reload the page.'
            : 'Verification is taking longer than expected. If nothing appears above, check your connection or reload the page.'}
        </p>
      )}
    </div>
  );
}
